/**
 * Transport layer. The only module that knows about HTTP.
 *
 * - Injects the access token (held in memory, never localStorage — see the security doc).
 * - Normalises every failure into ApiError { status, code, message, details }.
 * - Refreshes once on 401, then replays the original request.
 */

const BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

/**
 * For the few URLs the browser fetches itself — an `<iframe>` src, a download anchor — which
 * cannot go through the client below but must still point at the same API. Exported so that
 * nowhere has to hardcode `/api` and quietly aim at the wrong origin once this is deployed
 * with the API on a host of its own.
 */
export const apiUrl = (path) => `${BASE_URL}${path}`;

export const USE_MOCK = (import.meta.env.VITE_API_MODE || 'mock') === 'mock';

let accessToken = null;
let onUnauthorized = null;
let refreshing = null;

export function setAccessToken(token) {
  accessToken = token;
}

export function setUnauthorizedHandler(fn) {
  onUnauthorized = fn;
}

export class ApiError extends Error {
  constructor(status, code, message, details = []) {
    super(message || 'Something went wrong.');
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }

  get isPermission() {
    return this.status === 403;
  }

  get isNotFound() {
    return this.status === 404;
  }
}

async function parse(response) {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

/**
 * Exchange the HttpOnly refresh cookie for a new access token.
 *
 * Called proactively at app start: the access token lives in memory only, so after a
 * reload it is gone. Without this, every request on first paint would 401, refresh and
 * retry — which works, but fires a burst of avoidable failures and races several
 * concurrent refreshes. Concurrent callers share one in-flight promise.
 */
export async function refreshSession() {
  if (!refreshing) {
    refreshing = fetch(`${BASE_URL}/auth/refresh`, { method: 'POST', credentials: 'include' })
      .then(async (res) => {
        if (!res.ok) throw new ApiError(401, 'SESSION_EXPIRED', 'Your session has expired.');
        const body = await parse(res);
        accessToken = body?.data?.accessToken || null;
        return accessToken;
      })
      .finally(() => {
        refreshing = null;
      });
  }
  return refreshing;
}

async function request(path, { method = 'GET', body, headers = {}, signal, retry = true } = {}) {
  const init = {
    method,
    credentials: 'include',
    signal,
    headers: {
      Accept: 'application/json',
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...headers,
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  };

  let response;
  try {
    response = await fetch(`${BASE_URL}${path}`, init);
  } catch (error) {
    if (error.name === 'AbortError') throw error;
    throw new ApiError(0, 'NETWORK', 'Cannot reach the server. Check your connection and try again.');
  }

  // A 401 from the sign-in endpoints is the answer — a wrong password, a wrong or expired
  // code — not a session that needs refreshing. There is no session yet, so the refresh
  // would fail and its generic "your session has ended" would replace the message the
  // person actually has to read, such as how many attempts they have left.
  if (response.status === 401 && retry && !/^\/auth\/(login|mfa|refresh)\b/.test(path)) {
    try {
      await refreshSession();
      return request(path, { method, body, headers, signal, retry: false });
    } catch {
      onUnauthorized?.();
      throw new ApiError(401, 'UNAUTHENTICATED', 'Your session has ended. Please sign in again.');
    }
  }

  const payload = await parse(response);

  if (!response.ok) {
    const err = payload?.error || {};
    throw new ApiError(response.status, err.code || 'ERROR', err.message, err.details || []);
  }

  return payload?.data !== undefined ? payload.data : payload;
}

/** Fetch a binary/text body (CSV export, thumbnails) with the same auth and refresh rules. */
async function requestBlob(path, retry = true) {
  const response = await fetch(`${BASE_URL}${path}`, {
    credentials: 'include',
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
  });
  if (response.status === 401 && retry) {
    await refreshSession();
    return requestBlob(path, false);
  }
  if (!response.ok) {
    const payload = await parse(response);
    const err = payload?.error || {};
    throw new ApiError(response.status, err.code || 'ERROR', err.message);
  }
  return response.blob();
}

export const api = {
  blob: requestBlob,
  get: (path, options) => request(path, { ...options, method: 'GET' }),
  post: (path, body, options) => request(path, { ...options, method: 'POST', body }),
  patch: (path, body, options) => request(path, { ...options, method: 'PATCH', body }),
  put: (path, body, options) => request(path, { ...options, method: 'PUT', body }),
  delete: (path, options) => request(path, { ...options, method: 'DELETE' }),
};

/** Build a query string from an object, dropping empty values. */
export function qs(params = {}) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== null && v !== undefined && v !== '') search.set(k, v);
  });
  const str = search.toString();
  return str ? `?${str}` : '';
}

export default api;
